import React, { useState, useEffect, useCallback, useRef } from 'react';
import { emailTemplatesApi, ordersApi } from '@/services/api';

interface EmailTemplate {
  id: string;
  dealerId: string | null;
  parentTemplateId: string | null;
  name: string;
  description: string | null;
  templateType: string;
  subject: string;
  contentSections: Record<string, any>;
  mergeFields: string[];
  defaultCc: string | null;
  defaultBcc: string | null;
  isSystem: boolean;
  isActive: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

type View = 'library' | 'editor' | 'send';

const TYPE_BADGES: Record<string, { label: string; color: string }> = {
  sales_packet: { label: 'Estimate', color: 'bg-blue-500/20 text-blue-400' },
  promo: { label: 'Promo', color: 'bg-purple-500/20 text-purple-400' },
  non_visualizer_quote: { label: 'Quote', color: 'bg-green-500/20 text-green-400' },
  follow_up: { label: 'Follow-up', color: 'bg-amber-500/20 text-amber-400' },
  custom: { label: 'Custom', color: 'bg-gray-500/20 text-gray-400' },
};

export function EmailTemplatesPage() {
  const [view, setView] = useState<View>('library');
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewSubject, setPreviewSubject] = useState('');
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [toast, setToast] = useState('');

  const loadTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const data = await emailTemplatesApi.list();
      setTemplates(data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  useEffect(() => {
    if (toast) {
      const t = setTimeout(() => setToast(''), 3000);
      return () => clearTimeout(t);
    }
  }, [toast]);

  const showToast = (msg: string) => setToast(msg);

  const handleClone = async (id: string) => {
    try {
      const cloned = await emailTemplatesApi.clone(id);
      showToast(`Template cloned: ${cloned.name}`);
      await loadTemplates();
      setSelectedTemplate(cloned);
      setView('editor');
    } catch (err: any) {
      showToast(err.response?.data?.message || 'Failed to clone template');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this template? This cannot be undone.')) return;
    try {
      await emailTemplatesApi.delete(id);
      showToast('Template deleted');
      await loadTemplates();
    } catch (err: any) {
      showToast(err.response?.data?.message || 'Failed to delete');
    }
  };

  const handlePreview = async (template: EmailTemplate) => {
    try {
      const result = await emailTemplatesApi.preview(template.id);
      setPreviewHtml(result.html);
      setPreviewSubject(result.subject);
      setShowPreviewModal(true);
    } catch (err: any) {
      showToast(err.response?.data?.message || 'Failed to preview');
    }
  };

  const handleCopyHtml = async (template: EmailTemplate) => {
    try {
      const result = await emailTemplatesApi.exportHtml(template.id);
      await navigator.clipboard.writeText(result.html);
      showToast('HTML copied to clipboard');
    } catch (err: any) {
      showToast('Failed to copy HTML');
    }
  };

  const handleDownloadHtml = async (template: EmailTemplate) => {
    try {
      const result = await emailTemplatesApi.exportHtml(template.id);
      const blob = new Blob([result.html], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${template.name.replace(/\s+/g, '-').toLowerCase()}.html`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      showToast('Failed to download');
    }
  };

  const handleCreateCustom = () => {
    setSelectedTemplate(null);
    setView('editor');
  };

  const handleEdit = (template: EmailTemplate) => {
    setSelectedTemplate(template);
    setView('editor');
  };

  const handleSendModal = (template: EmailTemplate) => {
    setSelectedTemplate(template);
    setView('send');
  };

  const handleSaved = async () => {
    await loadTemplates();
    setView('library');
    showToast('Template saved');
  };

  const systemTemplates = templates.filter((t) => t.isSystem);
  const myTemplates = templates.filter((t) => !t.isSystem);

  return (
    <div className="max-w-7xl mx-auto">
      {/* Toast */}
      {toast && (
        <div className="fixed top-4 right-4 z-50 bg-keder-yellow text-black px-4 py-2 rounded-lg shadow-lg font-medium text-sm">
          {toast}
        </div>
      )}

      {/* Preview Modal */}
      {showPreviewModal && (
        <PreviewModal
          html={previewHtml}
          subject={previewSubject}
          onClose={() => setShowPreviewModal(false)}
        />
      )}

      {view === 'library' && (
        <TemplateLibrary
          systemTemplates={systemTemplates}
          myTemplates={myTemplates}
          loading={loading}
          error={error}
          onPreview={handlePreview}
          onClone={handleClone}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onCopyHtml={handleCopyHtml}
          onDownloadHtml={handleDownloadHtml}
          onCreateCustom={handleCreateCustom}
          onSend={handleSendModal}
        />
      )}

      {view === 'editor' && (
        <TemplateEditor
          template={selectedTemplate}
          onBack={() => { setView('library'); setSelectedTemplate(null); }}
          onSaved={handleSaved}
          showToast={showToast}
        />
      )}

      {view === 'send' && selectedTemplate && (
        <SendView
          template={selectedTemplate}
          onBack={() => { setView('library'); setSelectedTemplate(null); }}
          showToast={showToast}
        />
      )}
    </div>
  );
}

// ─── TEMPLATE LIBRARY ───────────────────────────────────────────────────────

function TemplateLibrary({
  systemTemplates,
  myTemplates,
  loading,
  error,
  onPreview,
  onClone,
  onEdit,
  onDelete,
  onCopyHtml,
  onDownloadHtml,
  onCreateCustom,
  onSend,
}: {
  systemTemplates: EmailTemplate[];
  myTemplates: EmailTemplate[];
  loading: boolean;
  error: string;
  onPreview: (t: EmailTemplate) => void;
  onClone: (id: string) => void;
  onEdit: (t: EmailTemplate) => void;
  onDelete: (id: string) => void;
  onCopyHtml: (t: EmailTemplate) => void;
  onDownloadHtml: (t: EmailTemplate) => void;
  onCreateCustom: () => void;
  onSend: (t: EmailTemplate) => void;
}) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-keder-yellow border-t-transparent" />
      </div>
    );
  }

  if (error) {
    return <div className="text-center py-20 text-red-400">{error}</div>;
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Email Templates</h1>
          <p className="text-gray-400 mt-1">Professional V-Track branded emails for your customers</p>
        </div>
        <button
          onClick={onCreateCustom}
          className="px-4 py-2 bg-keder-yellow text-black font-medium rounded-lg hover:bg-yellow-400 transition-colors"
        >
          + Create Template
        </button>
      </div>

      {/* V-Track System Templates */}
      <div>
        <h2 className="text-lg font-semibold mb-4">V-Track Templates</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {systemTemplates.map((t) => (
            <TemplateCard
              key={t.id}
              template={t}
              isSystem={true}
              onPreview={() => onPreview(t)}
              onClone={() => onClone(t.id)}
              onSend={() => onSend(t)}
            />
          ))}
        </div>
      </div>

      {/* My Templates */}
      <div>
        <h2 className="text-lg font-semibold mb-4">
          My Templates {myTemplates.length > 0 && <span className="text-gray-400 font-normal">({myTemplates.length})</span>}
        </h2>
        {myTemplates.length === 0 ? (
          <div className="border border-dashed border-gray-600 rounded-lg p-8 text-center">
            <p className="text-gray-400">No custom templates yet. Clone a V-Track template or create your own.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {myTemplates.map((t) => (
              <TemplateCard
                key={t.id}
                template={t}
                isSystem={false}
                onPreview={() => onPreview(t)}
                onEdit={() => onEdit(t)}
                onDelete={() => onDelete(t.id)}
                onCopyHtml={() => onCopyHtml(t)}
                onDownloadHtml={() => onDownloadHtml(t)}
                onSend={() => onSend(t)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── TEMPLATE CARD ──────────────────────────────────────────────────────────

function TemplateCard({
  template,
  isSystem,
  onPreview,
  onClone,
  onEdit,
  onDelete,
  onCopyHtml,
  onDownloadHtml,
  onSend,
}: {
  template: EmailTemplate;
  isSystem: boolean;
  onPreview: () => void;
  onClone?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onCopyHtml?: () => void;
  onDownloadHtml?: () => void;
  onSend?: () => void;
}) {
  const badge = TYPE_BADGES[template.templateType] || TYPE_BADGES.custom;

  return (
    <div className="card p-5 hover:border-gray-500 transition-colors">
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-semibold truncate">{template.name}</h3>
            <span className={`px-2 py-0.5 rounded text-xs font-medium ${badge.color}`}>{badge.label}</span>
            {isSystem && <span className="px-2 py-0.5 rounded text-xs font-medium bg-keder-yellow/20 text-keder-yellow">System</span>}
          </div>
          {template.description && (
            <p className="text-gray-400 text-sm line-clamp-2">{template.description}</p>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mt-3">
        <button onClick={onPreview} className="px-3 py-1.5 text-xs font-medium rounded bg-gray-700 text-gray-300 hover:bg-gray-600 transition-colors">
          Preview
        </button>
        {isSystem && onClone && (
          <button onClick={onClone} className="px-3 py-1.5 text-xs font-medium rounded bg-keder-yellow/20 text-keder-yellow hover:bg-keder-yellow/30 transition-colors">
            Clone to Customize
          </button>
        )}
        {!isSystem && onEdit && (
          <button onClick={onEdit} className="px-3 py-1.5 text-xs font-medium rounded bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 transition-colors">
            Edit
          </button>
        )}
        {onSend && (
          <button onClick={onSend} className="px-3 py-1.5 text-xs font-medium rounded bg-green-500/20 text-green-400 hover:bg-green-500/30 transition-colors">
            Send
          </button>
        )}
        {!isSystem && onCopyHtml && (
          <button onClick={onCopyHtml} className="px-3 py-1.5 text-xs font-medium rounded bg-gray-700 text-gray-300 hover:bg-gray-600 transition-colors">
            Copy HTML
          </button>
        )}
        {!isSystem && onDownloadHtml && (
          <button onClick={onDownloadHtml} className="px-3 py-1.5 text-xs font-medium rounded bg-gray-700 text-gray-300 hover:bg-gray-600 transition-colors">
            Download
          </button>
        )}
        {!isSystem && onDelete && (
          <button onClick={onDelete} className="px-3 py-1.5 text-xs font-medium rounded bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors">
            Delete
          </button>
        )}
      </div>
    </div>
  );
}

// ─── PREVIEW MODAL ──────────────────────────────────────────────────────────

function PreviewModal({ html, subject, onClose }: { html: string; subject: string; onClose: () => void }) {
  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b">
          <div>
            <p className="text-sm text-gray-500">Subject</p>
            <p className="font-medium text-gray-900">{subject}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1">
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
        <div className="flex-1 overflow-auto">
          <iframe
            srcDoc={html}
            className="w-full h-full min-h-[600px]"
            title="Email Preview"
            sandbox="allow-same-origin"
          />
        </div>
      </div>
    </div>
  );
}

// ─── TEMPLATE EDITOR ────────────────────────────────────────────────────────

function TemplateEditor({
  template,
  onBack,
  onSaved,
  showToast,
}: {
  template: EmailTemplate | null;
  onBack: () => void;
  onSaved: () => void;
  showToast: (msg: string) => void;
}) {
  const isNew = !template;
  const [name, setName] = useState(template?.name || '');
  const [description, setDescription] = useState(template?.description || '');
  const [subject, setSubject] = useState(template?.subject || '');
  const [templateType, setTemplateType] = useState(template?.templateType || 'custom');
  const [sections, setSections] = useState<Record<string, any>>(template?.contentSections || {});
  const [defaultCc, setDefaultCc] = useState(template?.defaultCc || '');
  const [defaultBcc, setDefaultBcc] = useState(template?.defaultBcc || '');
  const [saving, setSaving] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const previewTimer = useRef<any>(null);
  const [templateId, setTemplateId] = useState(template?.id || '');

  // Load initial preview
  useEffect(() => {
    if (templateId) {
      loadPreview();
    }
  }, [templateId]);

  // Debounced auto-save + preview refresh on section edits
  const refreshPreview = useCallback(() => {
    if (!templateId) return;
    if (previewTimer.current) clearTimeout(previewTimer.current);
    previewTimer.current = setTimeout(async () => {
      try {
        // Auto-save sections so preview reflects current edits
        await emailTemplatesApi.update(templateId, { contentSections: sections });
        const result = await emailTemplatesApi.preview(templateId);
        setPreviewHtml(result.html);
      } catch {
        // Ignore preview errors
      }
    }, 1200);
  }, [templateId, sections]);

  const loadPreview = async () => {
    if (!templateId) return;
    try {
      const result = await emailTemplatesApi.preview(templateId);
      setPreviewHtml(result.html);
    } catch {
      // Ignore preview errors
    }
  };

  const updateSection = (key: string, value: any) => {
    setSections((prev) => ({ ...prev, [key]: value }));
    refreshPreview();
  };

  const handleSave = async () => {
    if (!name.trim() || !subject.trim()) {
      showToast('Name and subject are required');
      return;
    }
    setSaving(true);
    try {
      if (isNew) {
        const created = await emailTemplatesApi.create({
          name,
          subject,
          templateType,
          description: description || undefined,
          contentSections: sections,
        });
        setTemplateId(created.id);
        showToast('Template created');
      } else {
        await emailTemplatesApi.update(templateId, {
          name,
          subject,
          description: description || undefined,
          contentSections: sections,
          defaultCc: defaultCc || '',
          defaultBcc: defaultBcc || '',
        });
        await loadPreview();
      }
      onSaved();
    } catch (err: any) {
      showToast(err.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const sectionFields = getSectionFields(templateType, sections);

  const renderSectionField = (field: SectionField) => {
    switch (field.type) {
      case 'text':
        return (
          <div key={field.key}>
            <label className="block text-sm text-gray-500 mb-1">{field.label}</label>
            <input
              value={sections[field.key] || ''}
              onChange={(e) => updateSection(field.key, e.target.value)}
              className="input-field w-full text-sm"
              placeholder={field.placeholder}
            />
          </div>
        );
      case 'textarea':
        return (
          <div key={field.key}>
            <label className="block text-sm text-gray-500 mb-1">{field.label}</label>
            <textarea
              value={sections[field.key] || ''}
              onChange={(e) => updateSection(field.key, e.target.value)}
              rows={4}
              className="input-field w-full text-sm resize-y"
              placeholder={field.placeholder}
            />
          </div>
        );
      case 'toggle':
        return (
          <div key={field.key} className="flex items-center justify-between py-1">
            <label className="text-sm text-gray-400">{field.toggleLabel || field.label}</label>
            <button
              type="button"
              onClick={() => updateSection(field.key, !sections[field.key])}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                sections[field.key] ? 'bg-keder-yellow' : 'bg-gray-600'
              }`}
            >
              <span className={`inline-block h-4 w-4 rounded-full bg-white transition-transform ${
                sections[field.key] ? 'translate-x-6' : 'translate-x-1'
              }`} />
            </button>
          </div>
        );
      case 'list':
        return (
          <div key={field.key}>
            <label className="block text-sm text-gray-500 mb-1">{field.label}</label>
            <ListEditor
              items={sections[field.key] || field.defaultItems || []}
              onChange={(items) => updateSection(field.key, items)}
            />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="text-gray-400 hover:text-white p-1">
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
          </button>
          <h1 className="text-xl font-bold">{isNew ? 'Create Template' : `Edit: ${template?.name}`}</h1>
        </div>
        <div className="flex gap-2">
          <button onClick={onBack} className="px-4 py-2 text-gray-400 hover:text-white transition-colors">Cancel</button>
          <button onClick={handleSave} disabled={saving} className="px-4 py-2 bg-keder-yellow text-black font-medium rounded-lg hover:bg-yellow-400 disabled:opacity-50 transition-colors">
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      {/* Editor layout */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* Left panel — form fields based on template type */}
        <div className="lg:col-span-2 space-y-4">
          <div className="card p-4 space-y-4">
            <div>
              <label className="block text-sm text-gray-500 mb-1">Template Name</label>
              <input value={name} onChange={(e) => setName(e.target.value)} className="input-field w-full text-sm" placeholder="e.g. Follow-up Email" />
            </div>
            <div>
              <label className="block text-sm text-gray-500 mb-1">Subject Line</label>
              <input value={subject} onChange={(e) => setSubject(e.target.value)} className="input-field w-full text-sm" placeholder="e.g. Your V-Track Screen Estimate" />
            </div>
            <div>
              <label className="block text-sm text-gray-500 mb-1">Default CC</label>
              <input
                value={defaultCc}
                onChange={(e) => setDefaultCc(e.target.value)}
                className="input-field w-full text-sm"
                placeholder="sales@yourdealer.com (comma-separate multiple)"
              />
              <p className="text-xs text-gray-400 mt-1">Auto-CC'd on every send of this template. Your customer sees it.</p>
            </div>
            <div>
              <label className="block text-sm text-gray-500 mb-1">Default BCC</label>
              <input
                value={defaultBcc}
                onChange={(e) => setDefaultBcc(e.target.value)}
                className="input-field w-full text-sm"
                placeholder="archive@yourdealer.com (comma-separate multiple)"
              />
              <p className="text-xs text-gray-400 mt-1">Silent copy — customer never sees this address.</p>
            </div>
          </div>

          {/* Template-type-specific content sections */}
          <div className="card p-4 space-y-4">
            <h2 className="text-sm font-semibold text-keder-yellow uppercase tracking-wide">Email Content</h2>
            <p className="text-xs text-gray-500">
              Use {'{{customer_first_name}}'}, {'{{dealer_company_name}}'}, etc. to auto-insert merge fields.
            </p>
            {sectionFields.map(renderSectionField)}
          </div>
        </div>

        {/* Right panel — live preview */}
        <div className="lg:col-span-3">
          <div className="bg-white rounded-lg overflow-hidden sticky top-4">
            <div className="bg-gray-100 px-4 py-2 border-b flex items-center justify-between">
              <span className="text-sm text-gray-500">Preview</span>
              {templateId && (
                <button onClick={loadPreview} className="text-xs text-blue-600 hover:text-blue-800">Refresh</button>
              )}
            </div>
            {previewHtml ? (
              <iframe
                srcDoc={previewHtml}
                className="w-full min-h-[700px]"
                title="Preview"
                sandbox="allow-same-origin"
              />
            ) : (
              <div className="flex items-center justify-center h-96 text-gray-400">
                {templateId ? 'Loading preview...' : 'Save template to see preview'}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── LIST EDITOR ────────────────────────────────────────────────────────────

function ListEditor({ items, onChange }: { items: string[]; onChange: (items: string[]) => void }) {
  const addItem = () => onChange([...items, '']);
  const removeItem = (idx: number) => onChange(items.filter((_, i) => i !== idx));
  const updateItem = (idx: number, value: string) => {
    const next = [...items];
    next[idx] = value;
    onChange(next);
  };

  return (
    <div className="space-y-2">
      {items.map((item, idx) => (
        <div key={idx} className="flex gap-2">
          <input
            value={item}
            onChange={(e) => updateItem(idx, e.target.value)}
            className="input-field flex-1 text-sm"
          />
          <button onClick={() => removeItem(idx)} className="text-red-400 hover:text-red-300 px-1">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
      ))}
      <button onClick={addItem} className="text-xs text-keder-yellow hover:text-yellow-300">+ Add item</button>
    </div>
  );
}

// ─── SEND VIEW ──────────────────────────────────────────────────────────────

function SendView({
  template,
  onBack,
  showToast,
}: {
  template: EmailTemplate;
  onBack: () => void;
  showToast: (msg: string) => void;
}) {
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [subjectOverride, setSubjectOverride] = useState(template.subject);
  const [cc, setCc] = useState('');
  const [sending, setSending] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');

  // Order linking state
  const [orderSearch, setOrderSearch] = useState('');
  const [orders, setOrders] = useState<any[]>([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [showOrderDropdown, setShowOrderDropdown] = useState(false);
  const [linkedOrder, setLinkedOrder] = useState<any>(null);
  const [orderMergeData, setOrderMergeData] = useState<Record<string, string> | null>(null);
  const [loadingMerge, setLoadingMerge] = useState(false);
  const orderSearchTimer = useRef<any>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowOrderDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Load preview (with order merge data if linked)
  const loadPreview = useCallback(async (mergeData?: Record<string, string>) => {
    try {
      const result = await emailTemplatesApi.preview(template.id, mergeData);
      setPreviewHtml(result.html);
    } catch {
      // Ignore preview errors
    }
  }, [template.id]);

  useEffect(() => {
    loadPreview(orderMergeData || undefined);
  }, [template.id, loadPreview, orderMergeData]);

  // Order search with debounce
  const searchOrders = useCallback(async (query: string) => {
    if (!query.trim()) {
      setOrders([]);
      return;
    }
    setLoadingOrders(true);
    try {
      const result = await ordersApi.list({ search: query, limit: 10 });
      const list = result.data || (result as any).items || [];
      setOrders(list);
      setShowOrderDropdown(true);
    } catch {
      setOrders([]);
    } finally {
      setLoadingOrders(false);
    }
  }, []);

  const handleOrderSearchChange = (value: string) => {
    setOrderSearch(value);
    if (orderSearchTimer.current) clearTimeout(orderSearchTimer.current);
    orderSearchTimer.current = setTimeout(() => searchOrders(value), 400);
  };

  // Link an order — fetch merge data and auto-fill
  const handleLinkOrder = async (order: any) => {
    setLinkedOrder(order);
    setShowOrderDropdown(false);
    setOrderSearch('');
    setLoadingMerge(true);
    try {
      const mergeData = await emailTemplatesApi.getOrderMergeData(template.id, order.id);
      setOrderMergeData(mergeData);
      // Auto-fill customer info from order
      if (mergeData.customer_name && !customerName) {
        setCustomerName(mergeData.customer_name);
      }
      if (mergeData.customer_email && !customerEmail) {
        setCustomerEmail(mergeData.customer_email);
      }
      // Refresh preview with order data
      loadPreview(mergeData);
    } catch (err: any) {
      showToast(err.response?.data?.message || 'Failed to load order data');
    } finally {
      setLoadingMerge(false);
    }
  };

  const handleUnlinkOrder = () => {
    setLinkedOrder(null);
    setOrderMergeData(null);
    loadPreview();
  };

  // Build merge data for sending
  const buildMergeData = (): Record<string, string> => {
    const data: Record<string, string> = {};
    // Include order merge data if linked
    if (orderMergeData) {
      Object.assign(data, orderMergeData);
    }
    if (customerName) {
      const parts = customerName.trim().split(/\s+/);
      data.customer_first_name = parts[0] || '';
      data.customer_last_name = parts.slice(1).join(' ') || '';
      data.customer_name = customerName;
    }
    if (customerEmail) {
      data.customer_email = customerEmail;
    }
    return data;
  };

  const handleSend = async () => {
    if (!customerEmail || !customerName) {
      showToast('Customer email and name are required');
      return;
    }
    setSending(true);
    try {
      const result = await emailTemplatesApi.send(template.id, {
        customerEmail,
        customerName,
        subject: subjectOverride || undefined,
        mergeData: buildMergeData(),
        cc: cc || undefined,
      });
      showToast(result.message);
      if (result.success) onBack();
    } catch (err: any) {
      showToast(err.response?.data?.message || 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  const handleSendTest = async () => {
    if (!testEmail) {
      showToast('Enter your email address for the test');
      return;
    }
    setSendingTest(true);
    try {
      const result = await emailTemplatesApi.sendTest(template.id, testEmail);
      showToast(result.message);
    } catch (err: any) {
      showToast(err.response?.data?.message || 'Failed to send test');
    } finally {
      setSendingTest(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="text-gray-400 hover:text-white p-1">
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
        </button>
        <h1 className="text-xl font-bold">Send: {template.name}</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* Left — Send form */}
        <div className="lg:col-span-2 space-y-4">
          {/* Link to Order */}
          <div className="card p-4 space-y-3">
            <h2 className="text-sm font-semibold text-keder-yellow uppercase tracking-wide">Link to Order</h2>
            <p className="text-xs text-gray-500">Auto-fill customer info and include order details in the email</p>

            {linkedOrder ? (
              <div className="flex items-center justify-between bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
                <div className="flex items-center gap-3 min-w-0">
                  <svg className="w-5 h-5 text-blue-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101M10.172 13.828a4 4 0 015.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" /></svg>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-blue-400 truncate">
                      {linkedOrder.orderNumber || linkedOrder.order_number}
                    </p>
                    <p className="text-xs text-gray-400 truncate">
                      {linkedOrder.sidemark || linkedOrder.customerLeadName || linkedOrder.customer_lead_name || 'No sidemark'}
                    </p>
                  </div>
                  {loadingMerge && (
                    <div className="animate-spin rounded-full h-4 w-4 border border-blue-400 border-t-transparent flex-shrink-0" />
                  )}
                </div>
                <button
                  onClick={handleUnlinkOrder}
                  className="text-gray-400 hover:text-red-400 p-1 flex-shrink-0"
                  title="Unlink order"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
            ) : (
              <div className="relative" ref={dropdownRef}>
                <input
                  value={orderSearch}
                  onChange={(e) => handleOrderSearchChange(e.target.value)}
                  onFocus={() => orders.length > 0 && setShowOrderDropdown(true)}
                  className="input-field w-full text-sm"
                  placeholder="Search by order #, sidemark, or customer..."
                />
                {loadingOrders && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <div className="animate-spin rounded-full h-4 w-4 border border-gray-400 border-t-transparent" />
                  </div>
                )}
                {showOrderDropdown && orders.length > 0 && (
                  <div className="absolute z-20 top-full mt-1 w-full bg-keder-dark border border-keder-gray rounded-lg shadow-xl max-h-60 overflow-y-auto">
                    {orders.map((o) => (
                      <button
                        key={o.id}
                        onClick={() => handleLinkOrder(o)}
                        className="w-full text-left px-3 py-2 hover:bg-keder-gray/50 transition-colors border-b border-keder-gray/30 last:border-0"
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium">{o.orderNumber || o.order_number}</span>
                          <span className="text-xs text-gray-500">{o.status}</span>
                        </div>
                        <p className="text-xs text-gray-400 truncate">
                          {[o.sidemark, o.customerLeadName || o.customer_lead_name].filter(Boolean).join(' — ') || 'No details'}
                        </p>
                      </button>
                    ))}
                  </div>
                )}
                {showOrderDropdown && orders.length === 0 && orderSearch.trim() && !loadingOrders && (
                  <div className="absolute z-20 top-full mt-1 w-full bg-keder-dark border border-keder-gray rounded-lg shadow-xl p-3 text-center text-sm text-gray-500">
                    No orders found
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Customer Info */}
          <div className="card p-4 space-y-4">
            <h2 className="text-sm font-semibold text-keder-yellow uppercase tracking-wide">Customer Info</h2>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Customer Name *</label>
              <input value={customerName} onChange={(e) => setCustomerName(e.target.value)} className="input-field w-full text-sm" placeholder="Sarah Mitchell" />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Customer Email *</label>
              <input value={customerEmail} onChange={(e) => setCustomerEmail(e.target.value)} type="email" className="input-field w-full text-sm" placeholder="customer@email.com" />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Subject</label>
              <input value={subjectOverride} onChange={(e) => setSubjectOverride(e.target.value)} className="input-field w-full text-sm" />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">CC (optional)</label>
              <input value={cc} onChange={(e) => setCc(e.target.value)} type="email" className="input-field w-full text-sm" placeholder="cc@email.com" />
            </div>
          </div>

          {/* Actions */}
          <div className="card p-4 space-y-3">
            <button
              onClick={handleSend}
              disabled={sending || !customerEmail || !customerName}
              className="w-full py-3 bg-keder-yellow text-black font-semibold rounded-lg hover:bg-yellow-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {sending ? 'Sending...' : 'Send to Customer'}
            </button>

            <div className="border-t border-keder-gray pt-3">
              <p className="text-xs text-gray-500 mb-2">Send a test to your inbox first</p>
              <div className="flex gap-2">
                <input
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  type="email"
                  placeholder="your@email.com"
                  className="input-field flex-1 text-sm"
                />
                <button
                  onClick={handleSendTest}
                  disabled={sendingTest || !testEmail}
                  className="px-4 py-2 bg-gray-700 text-gray-300 rounded hover:bg-gray-600 disabled:opacity-50 transition-colors text-sm"
                >
                  {sendingTest ? '...' : 'Test'}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Right — Preview */}
        <div className="lg:col-span-3">
          <div className="bg-white rounded-lg overflow-hidden sticky top-4">
            <div className="bg-gray-100 px-4 py-2 border-b flex items-center justify-between">
              <span className="text-sm text-gray-500">Email Preview</span>
              {linkedOrder && orderMergeData && (
                <span className="text-xs text-blue-600 font-medium">Showing order data</span>
              )}
            </div>
            {previewHtml ? (
              <iframe srcDoc={previewHtml} className="w-full min-h-[700px]" title="Preview" sandbox="allow-same-origin" />
            ) : (
              <div className="flex items-center justify-center h-96 text-gray-400">Loading preview...</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── SECTION FIELD DEFINITIONS ──────────────────────────────────────────────

interface SectionField {
  key: string;
  label: string;
  type: 'text' | 'textarea' | 'toggle' | 'list';
  placeholder?: string;
  toggleLabel?: string;
  defaultItems?: string[];
}

function getSectionFields(templateType: string, sections: Record<string, any>): SectionField[] {
  switch (templateType) {
    case 'sales_packet':
      return [
        { key: 'preheader', label: 'Preheader Text', type: 'textarea', placeholder: 'Preview text shown in inbox...' },
        { key: 'headline', label: 'Headline', type: 'text', placeholder: '{{customer_first_name}}, your estimate is ready.' },
        { key: 'subtext', label: 'Subtext', type: 'textarea', placeholder: "Here's the custom configuration..." },
        { key: 'show_visualizer_image', label: 'Visualizer Image', type: 'toggle', toggleLabel: 'Show visualizer screenshot' },
        { key: 'show_whats_included', label: "What's Included", type: 'toggle', toggleLabel: "Show What's Included section" },
        { key: 'whats_included', label: "What's Included Items", type: 'list', defaultItems: ['Motorized retractable screens', 'Wireless remote control', 'Professional installation', '5-year manufacturer warranty', 'V-Track retention system'] },
        { key: 'cta_headline', label: 'CTA Headline', type: 'text', placeholder: 'Ready to move forward?' },
        { key: 'cta_subtext', label: 'CTA Subtext', type: 'text', placeholder: 'Reply to this email or call us...' },
        { key: 'cta_button_text', label: 'CTA Button Text', type: 'text', placeholder: 'CALL {{dealer_phone_display}}' },
        { key: 'show_dealer_notes', label: 'Dealer Notes', type: 'toggle', toggleLabel: 'Show notes section' },
        { key: 'dealer_notes', label: 'Notes Text', type: 'textarea' },
        { key: 'terms', label: 'Terms', type: 'list', defaultItems: ['Estimate valid for 30 days', 'Final pricing confirmed after site measure', 'Lead time: 3-4 weeks from order', 'Payment: 50% deposit, balance on completion'] },
      ];

    case 'promo':
      return [
        { key: 'preheader', label: 'Preheader Text', type: 'textarea' },
        { key: 'brand_headline', label: 'Brand Headline', type: 'text', placeholder: 'Your Outdoor Space, Perfected.' },
        { key: 'brand_subtext', label: 'Brand Subtext', type: 'textarea' },
        { key: 'brand_cta_text', label: 'Hero CTA Text', type: 'text', placeholder: 'SEE YOUR SPACE' },
        { key: 'product_section_label', label: 'Product Section Label', type: 'text', placeholder: 'Choose Your Screen' },
        { key: 'product_section_heading', label: 'Product Section Heading', type: 'text', placeholder: 'Three screens. One system.' },
        { key: 'cta_headline', label: 'CTA Headline', type: 'text', placeholder: "See what's possible for your space." },
        { key: 'cta_subtext', label: 'CTA Subtext', type: 'text' },
        { key: 'cta_button_text', label: 'CTA Button Text', type: 'text', placeholder: 'SCHEDULE A CONSULTATION' },
        { key: 'cta_secondary_text', label: 'CTA Secondary Link', type: 'text', placeholder: 'or call {{dealer_phone_display}}' },
      ];

    case 'non_visualizer_quote':
      return [
        { key: 'preheader', label: 'Preheader Text', type: 'textarea' },
        { key: 'headline', label: 'Headline', type: 'text', placeholder: '{{customer_first_name}}, your estimate is ready.' },
        { key: 'subtext', label: 'Subtext', type: 'textarea' },
        { key: 'photo_caption', label: 'Photo Caption', type: 'textarea' },
        { key: 'next_steps', label: 'Next Steps', type: 'list', defaultItems: ['Review this estimate and let us know if you have questions', "We'll schedule a site measure to confirm dimensions", 'Once approved, your screens ship in 3-4 weeks'] },
        { key: 'cta_headline', label: 'CTA Headline', type: 'text', placeholder: 'Ready to move forward?' },
        { key: 'cta_subtext', label: 'CTA Subtext', type: 'text' },
        { key: 'cta_button_text', label: 'CTA Button Text', type: 'text' },
        { key: 'show_dealer_notes', label: 'Dealer Notes', type: 'toggle', toggleLabel: 'Show notes section' },
        { key: 'terms', label: 'Terms', type: 'list' },
      ];

    case 'follow_up':
      return [
        { key: 'preheader', label: 'Preheader Text', type: 'textarea' },
        { key: 'greeting', label: 'Greeting', type: 'text', placeholder: 'Hi {{customer_first_name}},' },
        { key: 'default_message', label: 'Message', type: 'textarea', placeholder: 'I wanted to follow up...' },
        { key: 'secondary_message', label: 'Secondary Message', type: 'textarea' },
        { key: 'resource_label', label: 'Resources Label', type: 'text', placeholder: 'Helpful Resources' },
        { key: 'resource_1_title', label: 'Resource 1 Title', type: 'text' },
        { key: 'resource_1_description', label: 'Resource 1 Description', type: 'text' },
        { key: 'resource_2_title', label: 'Resource 2 Title', type: 'text' },
        { key: 'resource_2_description', label: 'Resource 2 Description', type: 'text' },
        { key: 'resource_3_title', label: 'Resource 3 Title', type: 'text' },
        { key: 'resource_3_description', label: 'Resource 3 Description', type: 'text' },
        { key: 'cta_button_text', label: 'CTA Button Text', type: 'text', placeholder: 'REPLY TO THIS EMAIL' },
        { key: 'cta_secondary_text', label: 'CTA Secondary', type: 'text' },
        { key: 'show_seasonal_tip', label: 'Seasonal Tip', type: 'toggle', toggleLabel: 'Show seasonal tip' },
        { key: 'seasonal_tip', label: 'Seasonal Tip Text', type: 'textarea' },
      ];

    case 'custom':
    default:
      return [
        { key: 'preheader', label: 'Preheader Text', type: 'textarea' },
        { key: 'headline', label: 'Headline', type: 'text', placeholder: 'Hello {{customer_first_name}},' },
        { key: 'body_text', label: 'Body Text', type: 'textarea', placeholder: 'Thank you for your interest...' },
        { key: 'show_cta', label: 'Call to Action', type: 'toggle', toggleLabel: 'Show CTA section' },
        { key: 'cta_headline', label: 'CTA Headline', type: 'text', placeholder: 'Get in Touch' },
        { key: 'cta_subtext', label: 'CTA Subtext', type: 'text' },
        { key: 'cta_button_text', label: 'CTA Button Text', type: 'text', placeholder: 'CONTACT US' },
        { key: 'cta_button_url', label: 'CTA Button URL', type: 'text', placeholder: '#' },
      ];
  }
}
